import { parse } from 'yaml';

/**
 * The dialogue engine.
 *
 * Dialogue lives in a YAML file in the repo, not the database: branching
 * dialogue is a pain to author through a web form and a pleasure to author in
 * a text file with git history. (DESIGN.md §5)
 *
 * The single most important property of this module is that **it cannot lock
 * anyone out.** A malformed script, a `goto` pointing at nothing, a missing
 * portrait — every one of those returns null from `loadScript`, and the door
 * renders the plain fallback form instead. A typo in a YAML file must never be
 * able to keep the group out of the site the night of a screening.
 *
 * So: validate everything at load, throw nothing at runtime.
 */

export type Speaker = 'armane' | 'versace' | 'obojica';
export type Portrait = Speaker | 'ne';
export type InputKind = 'code' | 'credentials' | 'register';
export type NodeAction = 'open_door' | 'offer_bribe' | 'retry' | 'sing';

export interface Choice {
  text: string;
  goto: string;
}

export interface DialogueNode {
  id: string;
  speaker: Speaker;
  portrait: Portrait;
  /** Resolved per visit: a `pool` node picks a different line each time. */
  lines: string[];
  choices?: Choice[];
  input?: InputKind;
  onValid?: string;
  onInvalid?: string;
  action?: NodeAction;
  goto?: string;
  shake: boolean;
  /**
   * Both bouncers put a titovka on for this beat.
   *
   * A flag rather than a `speaker: armane_titovka`, because the hat is a
   * property of the moment, not of who is talking: at `poziv5` Versace speaks
   * and Armane is dimmed, and they are both still wearing one.
   */
  titovka: boolean;
  /** Set on nodes that must not wait for a tap. Drives the "Šefe." budget. */
  autoAdvanceMs?: number;
}

export interface Script {
  nodes: Map<string, DialogueNode>;
  overrides: Map<string, Map<string, Partial<DialogueNode>>>;
}

/**
 * Nodes the application enters directly, rather than reaching by `goto`.
 *
 * These are the seams where code names a node by string, so they are the only
 * places a rename can silently break the script. Keep this in sync with
 * BouncerScene — `test:dialogue` asserts each one exists and that no node in
 * the file is unreachable from this set.
 *
 * That second assertion is the one that matters. `povratak` sat in vrata.yaml
 * for a whole build with nothing pointing at it, which meant a member on a new
 * phone had no way to log in at all: every other path led to a single-use
 * invite code they had already spent.
 */
export const ENTRY_NODES = [
  'start', // no cookie
  'sefe', // a guard is on this device's payroll
  'ponuda', // authenticated, never bribed
  'zakljucano', // server reported a lockout
  'izdaja', // bribed the other guard
  'primljeno_armane',
  'primljeno_versace',
  'odbijeno', // pocketed the bill
  // The three ways the audition ends. Named by Sing, not reached by `goto`,
  // for the same reason the bribe outcomes are: the branch is decided by a
  // microphone, which the script has no way to ask about.
  'pevao',
  'cutao',
  'nije_dao_mikrofon',
] as const;

const SPEAKERS: readonly string[] = ['armane', 'versace', 'obojica'];
const PORTRAITS: readonly string[] = [...SPEAKERS, 'ne'];
const INPUTS: readonly string[] = ['code', 'credentials', 'register'];
const ACTIONS: readonly string[] = ['open_door', 'offer_bribe', 'retry', 'sing'];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function parseNode(raw: unknown, problems: string[]): DialogueNode | null {
  if (!isRecord(raw)) {
    problems.push('a node is not a mapping');
    return null;
  }
  const id = typeof raw.id === 'string' ? raw.id : null;
  if (!id) {
    problems.push('a node is missing `id`');
    return null;
  }

  const speaker = typeof raw.speaker === 'string' && SPEAKERS.includes(raw.speaker)
    ? (raw.speaker as Speaker)
    : null;
  if (!speaker) {
    problems.push(`${id}: \`speaker\` must be one of ${SPEAKERS.join(' | ')}`);
    return null;
  }

  const portrait =
    typeof raw.portrait === 'string' && PORTRAITS.includes(raw.portrait)
      ? (raw.portrait as Portrait)
      : speaker;

  // `text` and `pool` are the same thing to the engine; a pool just has more
  // than one candidate. Keeps the authoring format forgiving.
  const lines: string[] = [];
  if (typeof raw.text === 'string') lines.push(raw.text);
  if (Array.isArray(raw.pool)) {
    for (const line of raw.pool) if (typeof line === 'string') lines.push(line);
  }
  if (lines.length === 0) {
    problems.push(`${id}: needs \`text\` or a non-empty \`pool\``);
    return null;
  }

  let choices: Choice[] | undefined;
  if (Array.isArray(raw.choices)) {
    choices = [];
    for (const c of raw.choices) {
      if (isRecord(c) && typeof c.text === 'string' && typeof c.goto === 'string') {
        choices.push({ text: c.text, goto: c.goto });
      } else {
        problems.push(`${id}: a choice needs both \`text\` and \`goto\``);
        return null;
      }
    }
  }

  const input =
    typeof raw.input === 'string' && INPUTS.includes(raw.input) ? (raw.input as InputKind) : undefined;
  if (raw.input !== undefined && !input) {
    problems.push(`${id}: \`input\` must be one of ${INPUTS.join(' | ')}`);
    return null;
  }

  const action =
    typeof raw.action === 'string' && ACTIONS.includes(raw.action)
      ? (raw.action as NodeAction)
      : undefined;
  if (raw.action !== undefined && !action) {
    problems.push(`${id}: \`action\` must be one of ${ACTIONS.join(' | ')}`);
    return null;
  }

  if (input && !(typeof raw.on_valid === 'string' && typeof raw.on_invalid === 'string')) {
    problems.push(`${id}: an \`input\` node needs both \`on_valid\` and \`on_invalid\``);
    return null;
  }

  // A node with no way forward is a dead end, and a dead end in the only
  // login flow on the site is a lockout. Refuse the whole script.
  if (!choices && !input && !action && typeof raw.goto !== 'string') {
    problems.push(`${id}: dead end — needs one of goto / choices / input / action`);
    return null;
  }

  return {
    id,
    speaker,
    portrait,
    lines,
    ...(choices ? { choices } : {}),
    ...(input ? { input } : {}),
    ...(typeof raw.on_valid === 'string' ? { onValid: raw.on_valid } : {}),
    ...(typeof raw.on_invalid === 'string' ? { onInvalid: raw.on_invalid } : {}),
    ...(action ? { action } : {}),
    ...(typeof raw.goto === 'string' ? { goto: raw.goto } : {}),
    shake: raw.shake === true,
    titovka: raw.titovka === true,
    ...(typeof raw.auto_advance_ms === 'number' ? { autoAdvanceMs: raw.auto_advance_ms } : {}),
  };
}

/**
 * Parse and fully validate. Returns null on any problem — the caller falls
 * back to the plain form. `problems` is filled for the console, because the
 * person who has to fix it is the person who wrote the YAML.
 */
export function loadScript(source: string, problems: string[] = []): Script | null {
  let doc: unknown;
  try {
    doc = parse(source);
  } catch (err) {
    problems.push(`YAML did not parse: ${(err as Error).message}`);
    return null;
  }

  if (!isRecord(doc) || !Array.isArray(doc.nodes)) {
    problems.push('script needs a top-level `nodes:` list');
    return null;
  }

  const nodes = new Map<string, DialogueNode>();
  for (const raw of doc.nodes) {
    const node = parseNode(raw, problems);
    if (!node) return null;
    if (nodes.has(node.id)) {
      problems.push(`${node.id}: duplicate id`);
      return null;
    }
    nodes.set(node.id, node);
  }

  if (!nodes.has('start')) {
    problems.push('script has no `start` node');
    return null;
  }

  // Every jump must land somewhere. Checked here, once, rather than
  // discovered by a member standing at the door with a dead scene.
  for (const node of nodes.values()) {
    const targets = [
      node.goto,
      node.onValid,
      node.onInvalid,
      ...(node.choices?.map((c) => c.goto) ?? []),
    ].filter((t): t is string => typeof t === 'string');
    for (const target of targets) {
      if (!nodes.has(target)) {
        problems.push(`${node.id}: goto "${target}" does not exist`);
        return null;
      }
    }
  }

  const overrides = new Map<string, Map<string, Partial<DialogueNode>>>();
  if (isRecord(doc.overrides)) {
    for (const [key, block] of Object.entries(doc.overrides)) {
      if (!isRecord(block)) continue;
      const perNode = new Map<string, Partial<DialogueNode>>();
      for (const [nodeId, patchRaw] of Object.entries(block)) {
        if (!nodes.has(nodeId)) {
          problems.push(`override ${key}: node "${nodeId}" does not exist`);
          return null;
        }
        if (!isRecord(patchRaw)) continue;
        const patch: Partial<DialogueNode> = {};
        if (typeof patchRaw.speaker === 'string' && SPEAKERS.includes(patchRaw.speaker)) {
          patch.speaker = patchRaw.speaker as Speaker;
          patch.portrait = patchRaw.speaker as Portrait;
        }
        if (typeof patchRaw.portrait === 'string' && PORTRAITS.includes(patchRaw.portrait)) {
          patch.portrait = patchRaw.portrait as Portrait;
        }
        const lines: string[] = [];
        if (typeof patchRaw.text === 'string') lines.push(patchRaw.text);
        if (Array.isArray(patchRaw.pool)) {
          for (const l of patchRaw.pool) if (typeof l === 'string') lines.push(l);
        }
        if (lines.length > 0) patch.lines = lines;
        if (typeof patchRaw.titovka === 'boolean') patch.titovka = patchRaw.titovka;
        perNode.set(nodeId, patch);
      }
      overrides.set(key, perNode);
    }
  }

  return { nodes, overrides };
}

/**
 * Resolve a node for display: apply the override block if one is active, pick
 * a line from the pool, and fill template vars.
 *
 * Overrides are shallow-merged, so an override that only changes `text` keeps
 * the base node's routing. That is deliberate — an override should be able to
 * change what someone says without accidentally changing where the scene goes.
 */
export function resolveNode(
  script: Script,
  id: string,
  opts: { overrideKeys?: string[]; vars?: Record<string, string | number> } = {},
): (DialogueNode & { text: string }) | null {
  const base = script.nodes.get(id);
  if (!base) return null;

  let node: DialogueNode = base;
  for (const key of opts.overrideKeys ?? []) {
    const patch = script.overrides.get(key)?.get(id);
    if (patch) node = { ...node, ...patch };
  }

  const line = node.lines[Math.floor(Math.random() * node.lines.length)] ?? '';
  return { ...node, text: fillTemplate(line, opts.vars ?? {}) };
}

/**
 * `{{ime}}` → the value, `{{nepoznato}}` → empty string.
 *
 * Never throws on an unknown variable. Rendering a blank where a name should
 * be is a cosmetic bug; throwing here would take the door down.
 */
export function fillTemplate(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => {
    const value = vars[name];
    return value === undefined ? '' : String(value);
  });
}
